/**
 * Registers `sw.js` (roadmap item 13) — the one line of the offline story
 * that runs in the app itself, everything else being either build-time
 * (`build.ts`, `src/offline/precache-manifest.ts`) or the worker's own
 * (`src/sw.ts`).
 *
 * Service workers only ever register in a secure context (`https:` or
 * `localhost`) — the same constraint CLAUDE.md's WebGPU renderer already
 * lives under, so offline support and the 3D renderer share one deployment
 * requirement rather than adding a second. `navigator.serviceWorker` itself
 * is `undefined` in an insecure context, which is why the guard below is
 * enough on its own; there is no separate "are we secure" check to write.
 *
 * It also owns the two page-side recoveries the offline design leans on: the
 * `?unstale` URL hatch and the boot heartbeat
 * (`docs/engineering/offline-caching-design.md`). Both are here because both
 * have to happen before anything else claims the page.
 */

import { installBootHealthWatch } from "./boot-health.ts";
import { hasUnstaleFlag, withoutUnstaleFlag } from "./unstale-flag.ts";
import { unstale } from "./unstale.ts";

/**
 * `updateViaCache: "none"` is what stops a misconfigured host from pinning a
 * browser to a dead worker forever, and it is the load-bearing line in this
 * file.
 *
 * The whole update design (see `src/sw.ts`'s header) rests on the browser
 * re-fetching `/sw.js` and noticing its bytes changed. By default the browser
 * is allowed to answer that re-fetch out of the ordinary HTTP cache, subject
 * only to the spec's 24-hour ceiling on the script's own freshness lifetime.
 * Firefox bypasses the HTTP cache for these checks anyway; Safari honours the
 * headers. So when the stable server was briefly serving `/sw.js` with
 * `Cache-Control: public, max-age=31536000, immutable` (measured 2026-08-25),
 * Safari stayed on one old worker across every deploy and `/fly` hung on
 * "Starting engine…" — the old worker feeding an old, pathname-keyed
 * `/baked/*` cache to a page whose newer parts had come from elsewhere. A
 * hard reload cannot dislodge that: `Cmd-Option-R` reloads the *page*, not
 * the worker controlling it. The server headers are fixed, but the app should
 * not be one header away from unrecoverable, so it opts out here as well.
 */
export const UPDATE_VIA_CACHE: RegistrationOptions["updateViaCache"] = "none";

/**
 * Whether a `controllerchange` should reload now or wait for the `load` event.
 *
 * A new worker calls `skipWaiting()` in `install` and `clients.claim()` in
 * `activate`, so `controllerchange` can land at any point in a page's boot —
 * including while the document is still fetching the very chunks that boot
 * it. Reloading from inside that window is where the navigation issued by the
 * reload went unanswered in headless-Chromium testing (2 of ~8 runs,
 * 2026-08-25: page dead until the browser was restarted). Waiting for the
 * document to finish loading costs nothing — the page is already running the
 * old build either way — and reloads from a quiescent document instead of a
 * half-built one.
 */
export function shouldReloadNow(readyState: DocumentReadyState): boolean {
  return readyState === "complete";
}

/**
 * The page half of the `?unstale` escape hatch — and it is deliberately
 * *independent* of the worker half in `sw.ts`.
 *
 * The worker half answers an `?unstale` navigation straight from the network,
 * which recovers a page whose cached shell is poison. This half throws the
 * whole installation away, which recovers a *worker* that is broken enough
 * that it never passed the parameter through in the first place — including
 * one wedged before it could read a URL at all. Either alone is a recovery;
 * both together mean the hatch does not depend on the thing it is rescuing.
 *
 * The flag is stripped from the address bar *before* `unstale()` reloads, or
 * the reload arrives carrying `?unstale` and the app clears its cache on every
 * load for ever. Returns whether it fired, so the caller knows not to bother
 * registering a worker it is about to unregister.
 */
function handleUnstaleRequest(): boolean {
  if (typeof window === "undefined") return false;
  const search = window.location?.search ?? "";
  if (!hasUnstaleFlag(search)) return false;
  try {
    window.history?.replaceState(null, "", withoutUnstaleFlag(window.location.href));
  } catch {
    // A browser that refuses the rewrite (a `file:` origin, a policy) still
    // gets the clear and the reload; at worst the hatch runs a second time.
  }
  void unstale();
  return true;
}

export function registerServiceWorker(): void {
  if (handleUnstaleRequest()) return;
  installBootHealthWatch();
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  // Without this, every deploy lags one reload behind: the OLD worker serves
  // the OLD cached bundle to the reload that fetches the NEW worker, and the
  // fresh code only arrives on the reload after that. During rapid iteration
  // the user judges each fix against the previous build (lived experience,
  // 2026-08-21 — three bullet fixes in a row "not visible"). When the new
  // worker takes control mid-session, reload once so the page runs the build
  // its worker is serving. The flag guards against any reload loop.
  // A first visit fires controllerchange too, when the brand-new worker claims
  // the uncontrolled page — reload only when a controller is being *replaced*.
  const hadController = navigator.serviceWorker.controller !== null;
  let reloadScheduled = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloadScheduled) return;
    reloadScheduled = true;
    if (shouldReloadNow(document.readyState)) {
      window.location.reload();
      return;
    }
    window.addEventListener("load", () => window.location.reload(), { once: true });
  });
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { updateViaCache: UPDATE_VIA_CACHE }).catch((error: unknown) => {
      // Never fatal — the app already works fully online without it, and a
      // registration failure (an unsupported browser, a host that blocks the
      // route, `/sw.js` 404ing on a static host that hasn't wired it up yet)
      // should not be able to break the page that is asking for it.
      console.warn("red-opus: service worker registration failed", error);
    });
  });
}
